import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { Button } from "@/components/ui/button";
import { Clock, Crown, X, Sparkles } from "lucide-react";

export default function TrialCountdown() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser();
  const [showPopup, setShowPopup] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || !user?.trial_active || !user?.trial_ends) {
      setShowPopup(false);
      return;
    }

    // Check if already dismissed this session
    const dismissedKey = `trial_popup_dismissed_${user.user_id}`;
    if (sessionStorage.getItem(dismissedKey)) {
      setDismissed(true);
      return;
    }

    const trialEndDate = new Date(user.trial_ends);
    const now = new Date();
    const diffMs = trialEndDate - now;
    
    if (diffMs <= 0) {
      // Trial expired
      setShowPopup(false);
      return;
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    setDaysLeft(diffDays);
    setHoursLeft(diffHours);
    
    // Show popup when 3 days or less remaining, or first 2 hours of trial
    if (diffDays <= 3 || (diffDays >= 6 && diffHours <= 22)) {
      setTimeout(() => setShowPopup(true), 2000);
    }
  }, [user, isLoggedIn]);

  const handleDismiss = () => {
    const dismissedKey = `trial_popup_dismissed_${user?.user_id}`;
    sessionStorage.setItem(dismissedKey, 'true');
    setDismissed(true);
    setShowPopup(false);
  };

  if (!showPopup || dismissed) return null;

  const isUrgent = daysLeft <= 1;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${isUrgent ? 'border-2 border-red-500' : ''}`}>
        {/* Gradient Header */}
        <div className={`p-6 text-white ${isUrgent ? 'bg-gradient-to-r from-red-600 to-orange-500' : 'bg-gradient-to-r from-violet-600 to-purple-600'}`}>
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="trial-popup-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              {isUrgent ? <Clock className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="text-lg font-bold">
                {isUrgent ? '⚠️ Trial Ending Soon!' : '🎉 You\'re on Pro Trial!'}
              </h3>
              <p className="text-sm text-white/80">
                {isUrgent ? 'Act now to keep Pro features' : 'Enjoy premium features'}
              </p>
            </div>
          </div>

          {/* Countdown Display */}
          <div className="flex justify-center gap-4 py-4">
            <div className="text-center">
              <div className="bg-white/20 rounded-xl px-5 py-3 min-w-[70px]">
                <span className="text-3xl font-bold">{daysLeft}</span>
              </div>
              <span className="text-xs mt-1 block text-white/80">Days</span>
            </div>
            <div className="text-center">
              <div className="bg-white/20 rounded-xl px-5 py-3 min-w-[70px]">
                <span className="text-3xl font-bold">{hoursLeft}</span>
              </div>
              <span className="text-xs mt-1 block text-white/80">Hours</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            {isUrgent 
              ? 'Your trial expires soon. Upgrade now to continue using unlimited SEO tools, AI content, and advanced analytics.'
              : 'You have access to all Pro features including unlimited searches, AI content generation, and priority support.'
            }
          </p>

          {/* Features reminder */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {['Unlimited Keywords', 'AI Content', 'Site Audits', 'Analytics'].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                {feature}
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleDismiss}
              data-testid="trial-popup-later"
            >
              Maybe Later
            </Button>
            <Button 
              className={`flex-1 ${isUrgent ? 'bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600' : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700'}`}
              onClick={() => { handleDismiss(); navigate('/pricing'); }}
              data-testid="trial-popup-upgrade"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
